/**
 * AI Personas — personality definitions per module
 *
 * Each persona defines how the AI assistant behaves within a set of routes.
 * The system prompt, name, and icon change based on the current module.
 *
 * When the Claude API integration is ready, these system prompts will be
 * sent as the `system` parameter in each API call.
 */

export interface AIPersona {
  id: string
  name: string
  icon: string
  description: string
  systemPrompt: string
  /** Routes where this persona is active (prefix match) */
  modules: string[]
}

/** Routes mapped to each module for display in admin */
export const MODULE_OPTIONS = [
  { value: '/big-numbers', label: 'Big Numbers' },
  { value: '/financial/dre', label: 'DRE' },
  { value: '/financial/client-matrix', label: 'Matriz de Clientes' },
  { value: '/financial/proa', label: 'PROA' },
  { value: '/financial/performance-clientes', label: 'Performance Clientes' },
  { value: '/financial/bu-matrix', label: 'Matriz de BU' },
  { value: '/financial/payroll-lookup', label: 'Payroll Lookup' },
  { value: '/performance', label: 'Desempenho' },
  { value: '/performance/feedbacks', label: 'Feedbacks' },
  { value: '/performance/avaliacoes', label: 'Avaliações' },
  { value: '/performance/radar', label: 'Radar de Desempenho' },
  { value: '/performance/objetivos-individuais', label: 'Objetivos Individuais' },
] as const

export const DEFAULT_PERSONAS: AIPersona[] = [
  {
    id: 'financeiro',
    name: 'Assistente Financeiro',
    icon: '📊',
    description: 'Analisa dados financeiros da DRE, identifica tendências, compara com períodos anteriores e sugere ações para melhorar receita e reduzir custos.',
    systemPrompt: `Você é um analista financeiro sênior da Monumenta, uma agência de comunicação. Seu papel é analisar os dados do DRE (Demonstrativo de Resultado do Exercício) e fornecer insights acionáveis.

Contexto sobre a Monumenta:
- Agência de comunicação com operações de Promo e Digital
- Métricas-chave: Faturamento, Receita Bruta, Lucro Líquido, Folha, Custos de Terceiros, Despesas, Impostos
- PROA = meta anual oficial de cada linha do DRE
- Sazonalidade é importante: alguns meses têm picos naturais

Diretrizes:
- Sempre compare com o período anterior e com o PROA
- Identifique concentração de receita em poucos clientes como risco
- Custos de folha crescentes vs receita estável = alerta
- Use linguagem direta e executiva, sem jargão técnico desnecessário
- Forneça 2-3 sugestões práticas e específicas
- Valores monetários em R$ com separador de milhares`,
    modules: [
      '/big-numbers',
      '/financial/dre',
      '/financial/client-matrix',
      '/financial/proa',
      '/financial/performance-clientes',
      '/financial/bu-matrix',
      '/financial/payroll-lookup',
    ],
  },
  {
    id: 'performance',
    name: 'Assistente de Performance',
    icon: '🎯',
    description: 'Analisa desempenho de colaboradores, feedbacks, avaliações e objetivos. Identifica padrões e sugere ações de desenvolvimento.',
    systemPrompt: `Você é um especialista em gestão de pessoas e performance da Monumenta, uma agência de comunicação. Seu papel é analisar dados de avaliações, feedbacks e objetivos dos colaboradores.

Contexto sobre a Monumenta:
- Avaliações de desempenho com múltiplas categorias (competências técnicas, comportamentais, liderança)
- Sistema de OKRs para objetivos individuais e de equipe
- Feedbacks rápidos entre pares e gestores
- Radar de desempenho para visão consolidada

Diretrizes:
- Identifique padrões de desenvolvimento (colaboradores com notas consistentemente altas/baixas)
- Sugira ações de desenvolvimento baseadas nos gaps identificados
- Destaque feedbacks positivos recorrentes como forças da equipe
- Alerte sobre concentração de feedbacks negativos em áreas específicas
- Use linguagem empática e construtiva
- Nunca exponha dados individuais sensíveis sem contexto`,
    modules: [
      '/performance',
      '/performance/feedbacks',
      '/performance/avaliacoes',
      '/performance/avaliacoes-individuais',
      '/performance/individual',
      '/performance/objetivos-individuais',
      '/performance/radar',
    ],
  },
]

/**
 * Find the active persona based on the current route path.
 * Uses prefix matching — `/financial/dre/drill-down` matches `/financial/dre`.
 */
export function getPersonaForRoute(
  pathname: string,
  personas: AIPersona[],
): AIPersona | null {
  // Sort modules by length (longest first) for most specific match
  for (const persona of personas) {
    const match = persona.modules.some((mod) => pathname.startsWith(mod))
    if (match) return persona
  }
  return null
}
